import { HOME_BACKGROUND_COPY as C, type TvPlatformKey } from '@/lib/constants/appFactory';
import { APP_STATIC_HOSTS } from '@/lib/constants/appPreview';
import type { AppCfgCoreRow, AppCfgsDto } from '@/lib/types/App';
import type { FactoryBrandKit } from '@/lib/types/AppFactory';

/**
 * Where the home background stands for an app (3.46.0; appfactory ADR-0052 —
 * the image is a Home setting saved on its own, live at once; nothing waits
 * for an approval any more):
 * - none: no background image — nothing to switch on
 * - light: the app uses the light theme; the image is never shown
 * - live: the app has a background image (its Brand Kit's newest)
 */
export type HomeBackgroundKind = 'none' | 'light' | 'live';

export interface HomeBackgroundView {
  kind: HomeBackgroundKind;
  /** The switch as shown: the stored choice, off while there is no image (the choice is remembered for the next one). */
  checked: boolean;
  disabled: boolean;
  /** One sentence under the switch; null = nothing to add. */
  note: string | null;
  /** The picture the preview draws: the Brand Kit's newest, else the app's stored one. */
  previewUrl: string | null;
  /** The image as uploaded (the kit's source), for the slot's thumbnail; null = none stored. */
  imageUrl: string | null;
}

export interface HomeBackgroundInput {
  core: Pick<AppCfgCoreRow, 'homeBgEnabled' | 'homeBgKey'>;
  /** The app's newest Brand Kit revision; null = none. */
  kit: Pick<FactoryBrandKit, 'tileArtUrl' | 'homeBackgroundUrl'> | null;
  lightTheme: boolean;
  /** The white-label experience: images come from its own static host. */
  isReseller: boolean;
}

/** Pure rules of the home background card on the Brand Kit tab (design ADR-0015 D2 storage, ADR-0016 D5 rendering). */
export class HomeBackgroundUtils {
  static staticBase(isReseller: boolean): string {
    return isReseller ? APP_STATIC_HOSTS.whitelabel : APP_STATIC_HOSTS.streamhoster;
  }

  /** The public URL of `appCfg.core.homeBgKey` on the account's static host; null without a key. */
  static keyUrl(key: string | null | undefined, isReseller: boolean): string | null {
    const k = (key ?? '').trim().replace(/^\/+/, '');
    return k ? `${this.staticBase(isReseller)}/${k}` : null;
  }

  /** A static-host URL moved to the account's own static host (both serve the same files); others pass through. */
  static brandUrl(url: string | null | undefined, isReseller: boolean): string | null {
    const u = (url ?? '').trim();
    if (!u) return null;
    for (const host of Object.values(APP_STATIC_HOSTS)) {
      if (u.startsWith(`${host}/`)) return this.staticBase(isReseller) + u.slice(host.length);
    }
    return u;
  }

  /** The app uses the light theme: the web row's dark-mode flag (dark when the app has no web row — the server's rule). */
  static isLightTheme(app: Pick<AppCfgsDto, 'web'>): boolean {
    return app.web != null && !app.web.style.styleIsDarkMode;
  }

  static view(input: HomeBackgroundInput): HomeBackgroundView {
    const { core, kit, lightTheme, isReseller } = input;
    const imageUrl = this.brandUrl(kit?.tileArtUrl, isReseller);
    // The kit's newest picture is the app's (the api stores it at once); the
    // stored key stands in while a read has not caught up, or for a kit whose
    // picture the factory has yet to render.
    const previewUrl = this.brandUrl(kit?.homeBackgroundUrl, isReseller) ?? this.keyUrl(core.homeBgKey, isReseller);
    const chosen = core.homeBgEnabled === true;
    if (lightTheme) {
      return { kind: 'light', checked: chosen, disabled: true, note: C.noteLight, previewUrl: null, imageUrl };
    }
    if (previewUrl) {
      return { kind: 'live', checked: chosen, disabled: false, note: null, previewUrl, imageUrl };
    }
    return { kind: 'none', checked: false, disabled: true, note: C.noteNone, previewUrl: null, imageUrl };
  }

  /**
   * The Background image's help: the size, where it shows, and — for the TV
   * apps that draw it behind their icon — that a Generate picks it up (a
   * Roku-only or web-only app never reads about the Fire TV home screen).
   */
  static imageHelp(tv: readonly TvPlatformKey[]): string {
    const firetv = tv.includes('firetv');
    const googletv = tv.includes('googletv');
    if (firetv && googletv) return C.imageHelpBoth;
    if (firetv) return C.imageHelpFireTv;
    if (googletv) return C.imageHelpGoogleTv;
    return C.imageHelp;
  }
}
